import React from 'react';
import {
  SectionList,
  StyleProp,
  StyleSheet,
  Text,
  View,
  ViewStyle,
} from 'react-native';

import BillCard from './BillCard';

import {
  useMonthlyChargesBreakdownByBillIdQuery,
  useSingleBillStatementQuery,
} from 'apollo/queries/autogenerate/hooks';
import { BillStatement, Charge } from 'apollo/queries/autogenerate/schemas';
import { shouldHidePaymentMethod } from 'shared/modules/bill/utils/should-hide-payment-method';
import ChargeItem from 'shared/modules/common/components/organisms/ChargeItem';

type Props = {
  billId: string;
  /**
   * Disables the "Pay" button because the older bill must be paid first
   */
  isOldestUnpaidBill?: boolean;
  onChargeItemPress?: (charge: Charge) => void;
  onPayButtonPress?: (bill: BillStatement) => void;
  style?: StyleProp<ViewStyle>;
};

const BillDetails = (props: Props) => {
  /**
   * The list of charges returned by this query do not include negative charges
   * due to conversion of a Charge to a 3-pay installment.
   * So we need to use the MonthlyChargesBreakdownByBillIdQuery.
   */
  const { data: billStatementData } = useSingleBillStatementQuery({
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-only',
    variables: {
      id: props.billId,
    },
  });

  /**
   * More detailed breakdown of charges in a month,
   * including negative charges for 3-pay conversions and late fees.
   *
   * Q: What happens when a Charge is converted into 3-pay installments?
   * A: The original Charge amount is returned, that's why it becomes negative.
   *    And then creates separate new Charges for each installment breakdown.
   *    In case of 3-pay, a single Charge becomes 3 separate Charges.
   *    Other modes of installment (e.g. 24-month, 36-month) will behave similarly.
   */
  const { data: breakdownData } = useMonthlyChargesBreakdownByBillIdQuery({
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-only',
    variables: {
      billStatementId: props.billId,
    },
  });

  // The breakdown is in chronological order, the most recent on top.
  const chargesBreakdown =
    breakdownData?.monthlyChargesBreakdownByBillId.charges || [];

  if (!billStatementData) {
    return null;
  }

  const hidePaymentMethod = shouldHidePaymentMethod(
    billStatementData.billStatement.unpaidAmount,
    billStatementData.billStatement.statusV2
  );

  return (
    <View style={[style.root, props.style]}>
      {billStatementData && (
        <>
          <BillCard
            bill={billStatementData.billStatement as BillStatement}
            style={style.billCard}
            isOldestUnpaidBill={props.isOldestUnpaidBill}
            onPayButtonPress={props.onPayButtonPress}
            id="bill-detail-card"
            showPaymentMethod={!hidePaymentMethod}
          />
          {chargesBreakdown.length > 0 && (
            <SectionList
              sections={[
                {
                  title: 'ご利用明細',
                  data: chargesBreakdown,
                },
              ]}
              renderSectionHeader={(info) => (
                <Text style={style.sectionHeader}>{info.section.title}</Text>
              )}
              ItemSeparatorComponent={() => (
                <View style={style.separator}>
                  <View style={style.separatorLine} />
                </View>
              )}
              renderItem={({ item, index, section }) => (
                <ChargeItem
                  charge={item as Charge}
                  onPress={props.onChargeItemPress}
                  style={[
                    style.sectionItem,
                    !index && style.sectionItemFirst,
                    index === section.data.length - 1 && style.sectionItemLast,
                  ]}
                  id={`bill-details-charge-item-${index}`}
                />
              )}
              style={style.section}
            />
          )}
          <Text style={[style.text, style.warningTextTitle]}>※ご注意</Text>
          <Text style={[style.text, style.warningText]}>
            翌月一回払いを除き、商品の種類又は品質に関して契約の内容に適合しない場合や、役務の未提供などを理由に支払いを止めることができる場合があります。その場合は、ペイディカスタマーサポートへご連絡ください。ただし、予め販売店と交渉を行なうよう努めていただくものとします｡
          </Text>
          <Text style={[style.text, style.disclaimerText]}>
            キャンセルがある場合は、{'\n'}
            合計金額と請求金額が異なることがあります。
          </Text>
        </>
      )}
    </View>
  );
};

const style = StyleSheet.create({
  root: {
    backgroundColor: '#F6F8FA',
    minHeight: 500,
    padding: 16,
  },
  billCard: {
    marginBottom: 24,
    marginTop: -16,
    marginHorizontal: -16,
  },
  // SECTION
  section: {
    marginBottom: 24,
  },
  sectionHeader: {
    color: '#607D8B',
    fontWeight: 'bold',
    marginBottom: 16,
  },
  sectionItem: {
    backgroundColor: 'white',
    padding: 16,
  },
  sectionItemFirst: {
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
  },
  sectionItemLast: {
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
  },
  separator: {
    backgroundColor: 'white',
  },
  separatorLine: {
    height: StyleSheet.hairlineWidth,
    backgroundColor: '#EEF0F2',
    marginHorizontal: 16,
  },
  text: {
    fontSize: 12,
    color: '#586065',
  },
  warningTextTitle: {
    fontWeight: 'bold',
    marginBottom: 8,
  },
  warningText: {
    marginBottom: 24,
  },
  disclaimerText: {
    textAlign: 'center',
  },
});

export default BillDetails;
